import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navLinks = [
  { name: "HOME", path: "/" },
  { name: "ROOMS", path: "/rooms" },
  { name: "GALLERY", path: "/gallery" },
  { name: "ABOUT", path: "/about" },
  { name: "ATTRACTIONS", path: "/attractions" },
  { name: "CONTACT", path: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <nav
      data-testid="navbar"
      className="sticky top-0 z-50 border-b-2 border-ink bg-sand/90 backdrop-blur-md"
    >
      <div className="relative">
        <div className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-2"
          >
            <span className="font-anton text-2xl tracking-wider text-ink stencil-text sm:text-3xl">
              SEAS THE BAY
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden items-center gap-1 lg:flex">
            {navLinks.slice(0, 4).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`wavy-underline px-4 py-2 font-outfit text-sm font-medium tracking-widest uppercase transition-colors ${
                  location.pathname === link.path
                    ? "text-coral"
                    : "text-ink hover:text-coral"
                }`}
              >
                {link.name}
              </Link>
            ))}

            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="nav-more-dropdown"
                className="wavy-underline flex items-center gap-1 px-4 py-2 font-outfit text-sm font-medium tracking-widest uppercase text-ink hover:text-coral transition-colors"
              >
                MORE <ChevronDown className="h-3 w-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="border-2 border-ink bg-sand shadow-brutal"
                align="end"
              >
                {navLinks.slice(4).map((link) => (
                  <DropdownMenuItem key={link.path} asChild>
                    <Link
                      to={link.path}
                      data-testid={`nav-dropdown-${link.name.toLowerCase()}`}
                      className="font-outfit text-sm tracking-wider uppercase cursor-pointer"
                    >
                      {link.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild>
                  <Link
                    to="/privacy"
                    data-testid="nav-dropdown-privacy"
                    className="font-outfit text-sm tracking-wider uppercase cursor-pointer"
                  >
                    PRIVACY
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    to="/terms"
                    data-testid="nav-dropdown-terms"
                    className="font-outfit text-sm tracking-wider uppercase cursor-pointer"
                  >
                    TERMS
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/contact"
              data-testid="nav-book-now-btn"
              className="ml-4 bg-ink px-6 py-2 font-anton text-sm tracking-widest text-sand uppercase transition-all hover:-translate-y-0.5 hover:shadow-brutal-teal btn-spray"
            >
              BOOK NOW
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              data-testid="mobile-menu-trigger"
              className="lg:hidden"
            >
              {open ? (
                <X className="h-6 w-6 text-ink" />
              ) : (
                <Menu className="h-6 w-6 text-ink" />
              )}
            </SheetTrigger>
            <SheetContent
              side="right"
              className="border-l-2 border-ink bg-sand w-72"
            >
              <SheetTitle className="font-anton text-xl tracking-wider text-ink stencil-text">
                MENU
              </SheetTitle>
              <div className="mt-8 flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                    onClick={() => setOpen(false)}
                    className={`border-b border-ink/20 px-2 py-3 font-outfit text-base tracking-widest uppercase transition-colors ${
                      location.pathname === link.path
                        ? "text-coral"
                        : "text-ink hover:text-coral"
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <Link
                  to="/contact"
                  data-testid="mobile-book-now-btn"
                  onClick={() => setOpen(false)}
                  className="mt-4 bg-ink px-6 py-3 text-center font-anton text-sm tracking-widest text-sand uppercase shadow-brutal-teal"
                >
                  BOOK NOW
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
